import { useEffect, useRef, useState, type ComponentType } from "react";
import { useReducedMotion } from "framer-motion";
import type { DemoKind } from "@/data/projects";
import { play } from "@/lib/sound";

const NOTE = "font-mono text-[10.5px] text-inkfaint mt-2";

export function DocDemo() {
    const [extracted, setExtracted] = useState(false);
    const run = () => {
        play("click");
        setExtracted(true);
    };
    return (
        <div>
            <div className="rounded-xl border border-sand bg-sandsoft/50 p-3 font-mono text-[11.5px] text-inksoft">
                <p className="font-bold text-ink">pricing_doc_scan.pdf</p>
                <p>ITEM 2291 — condenser unit · qty ▓▓ · rate ▓▓▓</p>
                <p>ITEM 2292 — mounting kit · qty ▓▓ · rate ▓▓▓</p>
                <p>handwritten note: "confirm discount with vendor?"</p>
            </div>
            {!extracted ? (
                <button data-testid="demo-doc-extract" className="btn btn-blue btn-sm mt-3" onClick={run}>
                    Extract structured data
                </button>
            ) : (
                <div data-testid="demo-doc-result" className="pop-in mt-3 overflow-hidden rounded-xl border border-sand">
                    <table className="w-full font-mono text-[11.5px]">
                        <thead className="bg-elecsoft text-elec">
                            <tr>
                                <th className="px-2 py-1.5 text-left">sku</th>
                                <th className="px-2 py-1.5 text-left">item</th>
                                <th className="px-2 py-1.5 text-right">qty</th>
                                <th className="px-2 py-1.5 text-right">rate</th>
                            </tr>
                        </thead>
                        <tbody className="text-ink">
                            <tr className="border-t border-sand">
                                <td className="px-2 py-1.5">2291</td>
                                <td className="px-2 py-1.5">condenser unit</td>
                                <td className="px-2 py-1.5 text-right">4</td>
                                <td className="px-2 py-1.5 text-right">—</td>
                            </tr>
                            <tr className="border-t border-sand">
                                <td className="px-2 py-1.5">2292</td>
                                <td className="px-2 py-1.5">mounting kit</td>
                                <td className="px-2 py-1.5 text-right">12</td>
                                <td className="px-2 py-1.5 text-right">—</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
            <p className={NOTE}>OCR → language model → embeddings → structured JSON. simulation, not a live system.</p>
        </div>
    );
}

const RAG_STEPS = ["your question", "query rewrite", "chromadb retrieval", "grounded answer + citations"];

export function RagDemo() {
    const reduce = useReducedMotion();
    const [active, setActive] = useState(-1);
    const [running, setRunning] = useState(false);
    const timers = useRef<number[]>([]);

    useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

    const run = () => {
        timers.current.forEach((t) => window.clearTimeout(t));
        setActive(-1);
        setRunning(true);
        const gap = reduce ? 60 : 620;
        RAG_STEPS.forEach((_, i) =>
            timers.current.push(
                window.setTimeout(() => {
                    setActive(i);
                    play("click");
                    if (i === RAG_STEPS.length - 1) setRunning(false);
                }, i * gap),
            ),
        );
    };

    return (
        <div>
            <div className="flex flex-wrap gap-2">
                {["how does it cite sources?", "what if my query is vague?"].map((q) => (
                    <button key={q} data-testid="demo-rag-preset" className="chip hover:bg-sandsoft" onClick={() => run()}>
                        {q}
                    </button>
                ))}
            </div>
            <button data-testid="demo-rag-run" className="btn btn-blue btn-sm mt-3" onClick={run} disabled={running}>
                Run pipeline
            </button>
            <ol className="mt-3 space-y-1.5">
                {RAG_STEPS.map((s, i) => (
                    <li
                        key={s}
                        className={`rounded-lg border px-3 py-1.5 font-mono text-[11.5px] transition-colors ${
                            active >= i ? "border-elec bg-elecsoft text-ink" : "border-sand text-inkfaint"
                        }`}
                    >
                        {i + 1}. {s}
                    </li>
                ))}
            </ol>
            {active === RAG_STEPS.length - 1 && (
                <div data-testid="demo-rag-answer" className="pop-in mt-3 rounded-xl border-2 border-sage bg-sagesoft/50 p-3 text-[12.5px]">
                    citations stream inline as [1] [2] — every claim links back to the chunk it came from. no vibes-only answers.
                </div>
            )}
            <p className={NOTE}>sample flow — simulation, not a live model.</p>
        </div>
    );
}

export function VoiceDemo() {
    const [live, setLive] = useState(true);
    const bars = Array.from({ length: 22 }, (_, i) => i);
    return (
        <div>
            <div
                data-testid="demo-voice-wave"
                className={`flex h-16 items-center justify-center gap-1 rounded-xl border border-sand bg-sandsoft/50 px-3 ${live ? "" : "paused"}`}
            >
                {bars.map((i) => (
                    <span
                        key={i}
                        className="wave-bar"
                        style={{ height: `${18 + ((i * 37) % 44)}px`, animationDelay: `${(i % 7) * 0.12}s` }}
                    />
                ))}
            </div>
            <div className="mt-3 flex gap-2">
                {live ? (
                    <button data-testid="demo-voice-interrupt" className="btn btn-coral btn-sm" onClick={() => { setLive(false); play("click"); }}>
                        Interrupt the agent
                    </button>
                ) : (
                    <button data-testid="demo-voice-resume" className="btn btn-sm" onClick={() => { setLive(true); play("click"); }}>
                        Let it keep talking
                    </button>
                )}
            </div>
            {!live && (
                <p data-testid="demo-voice-status" className="pop-in mt-3 rounded-xl border-2 border-sage bg-sagesoft/50 p-3 text-[12.5px]">
                    interruption handled gracefully — the agent stopped mid-word (~0.3s) and listened. turn-taking is the whole game.
                </p>
            )}
            <p className={NOTE}>audio waveform (simulated) — no microphone is used, nothing is recorded.</p>
        </div>
    );
}

const REVIEW_COMMENTS = [
    "nit: consider naming this variable after what it holds, not its type.",
    "this loop re-creates the array each pass — hoist it. (suggested fix attached as commit 8841f2)",
];

export function ReviewDemo() {
    const [shown, setShown] = useState(0);
    const show = () => {
        REVIEW_COMMENTS.forEach((_, i) =>
            window.setTimeout(() => {
                setShown(i + 1);
                play("click");
            }, i * 700),
        );
    };
    return (
        <div>
            <div className="rounded-xl border border-sand bg-sandsoft/50 p-3 font-mono text-[11.5px]">
                <p className="font-bold text-ink">fix: estimate rounding (#42)</p>
                <p className="text-sage">+ total = round(value * 100) / 100</p>
                <p className="text-coral">- total = value.toFixed(2)</p>
            </div>
            {shown === 0 && (
                <button data-testid="demo-review-run" className="btn btn-blue btn-sm mt-3" onClick={show}>
                    Request AI review
                </button>
            )}
            {shown > 0 && (
                <ul className="mt-3 space-y-2" data-testid="demo-review-comments">
                    {REVIEW_COMMENTS.slice(0, shown).map((c) => (
                        <li key={c} className="pop-in rounded-xl border-2 border-lav bg-lavsoft/40 p-2.5 text-[12.5px]">
                            {c}
                        </li>
                    ))}
                </ul>
            )}
            <p className={NOTE}>sample review on a fictional PR — simulation.</p>
        </div>
    );
}

const CAPTIONS = [
    "a person walks into frame and waves.",
    "the scene cuts to a crowded market street.",
    "close-up: hands typing on a keyboard.",
    "the camera pans up to a grey sky.",
];

export function CaptionDemo() {
    const [i, setI] = useState(0);
    return (
        <div>
            <div className="flex gap-1.5">
                {CAPTIONS.map((_, f) => (
                    <div
                        key={f}
                        className={`h-12 flex-1 rounded-md border-2 ${f === i ? "border-elec bg-elecsoft" : "border-sand bg-sandsoft/60"}`}
                    />
                ))}
            </div>
            <p data-testid="demo-caption-text" className="mt-3 rounded-xl bg-termbg p-3 font-mono text-[12px] text-termgreen">
                caption {i + 1}/{CAPTIONS.length}: {CAPTIONS[i]}
            </p>
            <button
                data-testid="demo-caption-next"
                className="btn btn-blue btn-sm mt-3"
                onClick={() => {
                    setI((v) => (v + 1) % CAPTIONS.length);
                    play("click");
                }}
            >
                Next caption
            </button>
            <p className={NOTE}>frames simplified — research demo, not a live model.</p>
        </div>
    );
}

const NODES = [
    { id: "frames", x: 60, y: 40 },
    { id: "captions", x: 180, y: 30 },
    { id: "objects", x: 70, y: 110 },
    { id: "actions", x: 190, y: 100 },
    { id: "knowledge graph", x: 280, y: 55 },
];
const EDGES: [number, number][] = [
    [0, 1],
    [0, 2],
    [1, 3],
    [2, 4],
    [3, 4],
    [1, 4],
];

export const DEMOS: Record<DemoKind, ComponentType> = {
    doc: DocDemo,
    rag: RagDemo,
    voice: VoiceDemo,
    review: ReviewDemo,
    caption: CaptionDemo,
    graph: GraphDemo,
    similar: SimilarDemo,
    estimate: EstimateDemo,
};

const CANDIDATES = [
    { sku: "VVS 184-1240", name: "brass ball valve 3/4\"", shared: 7, total: 9 },
    { sku: "VVS 184-1255", name: "brass ball valve 3/4\" (long handle)", shared: 6, total: 10 },
    { sku: "VVS 190-0042", name: "stainless ball valve 1\"", shared: 4, total: 11 },
    { sku: "VVS 221-7781", name: "pipe clamp, 22 mm", shared: 1, total: 12 },
];

export function SimilarDemo() {
    const [run, setRun] = useState(false);
    const scored = CANDIDATES.map((c) => ({ ...c, j: c.shared / c.total })).sort((a, b) => b.j - a.j);
    return (
        <div>
            <div className="rounded-xl border border-sand bg-sandsoft/50 p-3 font-mono text-[11.5px] text-inksoft">
                <p className="font-bold text-ink">out of stock — VVS 184-1230 · brass ball valve 3/4"</p>
                <p>attributes: material, size, connection, pressure class, handle, finish…</p>
            </div>
            {!run ? (
                <button
                    data-testid="demo-similar-run"
                    className="btn btn-blue btn-sm mt-3"
                    onClick={() => {
                        play("click");
                        setRun(true);
                    }}
                >
                    Find alternatives
                </button>
            ) : (
                <div data-testid="demo-similar-result" className="pop-in mt-3 space-y-1.5">
                    {scored.map((c) => {
                        const pass = c.j >= 0.5;
                        return (
                            <div
                                key={c.sku}
                                className="flex items-center gap-3 rounded-xl border border-sand bg-paper px-3 py-2 font-mono text-[11.5px]"
                            >
                                <span className="w-24 flex-none text-inkfaint">{c.sku}</span>
                                <span className="min-w-0 flex-1 truncate text-ink">{c.name}</span>
                                <span className={pass ? "text-sage" : "text-inkfaint"}>J = {c.j.toFixed(2)}</span>
                                <span className={`badge ${pass ? "border-sage text-sage" : ""}`}>
                                    {pass ? "viable" : "below threshold"}
                                </span>
                            </div>
                        );
                    })}
                    <p className={NOTE}>jaccard = shared attributes / union of attributes · threshold 0.50 · feedback nudges it</p>
                </div>
            )}
        </div>
    );
}

const LINES = [
    { task: "demolition — interior walls", qty: "48 m²", rate: "310", total: "14,880" },
    { task: "masonry — new partition", qty: "26 m²", rate: "540", total: "14,040" },
    { task: "painting — two coats", qty: "112 m²", rate: "95", total: "10,640" },
];

export function EstimateDemo() {
    const [imported, setImported] = useState(false);
    return (
        <div>
            <div className="rounded-xl border border-sand bg-sandsoft/50 p-3 font-mono text-[11.5px] text-inksoft">
                <p className="font-bold text-ink">tender_udbud_2025.pdf</p>
                <p>14 pages · mixed tables and prose · one spreadsheet attached for flavour</p>
            </div>
            {!imported ? (
                <button
                    data-testid="demo-estimate-import"
                    className="btn btn-blue btn-sm mt-3"
                    onClick={() => {
                        play("click");
                        setImported(true);
                    }}
                >
                    Import tender
                </button>
            ) : (
                <div data-testid="demo-estimate-result" className="pop-in mt-3 overflow-hidden rounded-xl border border-sand">
                    <table className="w-full font-mono text-[11.5px]">
                        <thead className="bg-elecsoft text-elec">
                            <tr>
                                <th className="px-2 py-1.5 text-left">task</th>
                                <th className="px-2 py-1.5 text-right">qty</th>
                                <th className="px-2 py-1.5 text-right">rate</th>
                                <th className="px-2 py-1.5 text-right">total</th>
                            </tr>
                        </thead>
                        <tbody className="text-ink">
                            {LINES.map((l) => (
                                <tr key={l.task} className="border-t border-sand">
                                    <td className="px-2 py-1.5">{l.task}</td>
                                    <td className="px-2 py-1.5 text-right">{l.qty}</td>
                                    <td className="px-2 py-1.5 text-right">{l.rate}</td>
                                    <td className="px-2 py-1.5 text-right">{l.total}</td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-ink bg-sandsoft/60 font-bold">
                                <td className="px-2 py-1.5" colSpan={3}>
                                    estimate subtotal
                                </td>
                                <td className="px-2 py-1.5 text-right">39,560</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className={`${NOTE} px-2 pb-2`}>every imported line lands on the canvas as editable — the human signs off, not the model</p>
                </div>
            )}
        </div>
    );
}

export function GraphDemo() {
    const [sel, setSel] = useState<number | null>(null);
    return (
        <div>
            <svg viewBox="0 0 330 150" className="w-full max-w-sm rounded-xl border border-sand bg-sandsoft/50">
                {EDGES.map(([a, b], i) => {
                    const hot = sel !== null && (a === sel || b === sel);
                    return (
                        <line
                            key={i}
                            x1={NODES[a].x}
                            y1={NODES[a].y}
                            x2={NODES[b].x}
                            y2={NODES[b].y}
                            stroke={hot ? "#FF6B6B" : "#E2DDD3"}
                            strokeWidth={hot ? 3 : 2}
                        />
                    );
                })}
                {NODES.map((n, i) => (
                    <g key={n.id} data-testid={`demo-graph-node-${i}`} onClick={() => { setSel(i); play("click"); }} className="cursor-pointer">
                        <circle cx={n.x} cy={n.y} r={16} fill={sel === i ? "#FEF08A" : "#FFFDF9"} stroke="#1C1917" strokeWidth="2" />
                        <text x={n.x} y={n.y - 22} textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill="#4A463D">
                            {n.id}
                        </text>
                    </g>
                ))}
            </svg>
            <p className={NOTE}>
                {sel === null ? "click a node to trace its edges." : `edges of "${NODES[sel].id}" highlighted.`} from the NITK research:
                video → captions → entities → graph.
            </p>
        </div>
    );
}
